import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Switch } from "@/components/ui/switch";
import {
  useGetChatSettingsQuery,
  useSetChannelNoticeMutation,
} from "@/services/(my-space)/api.chat";
import {
  CHANNEL_NOTICE_KINDS,
  DEFAULT_CHAT_CHANNEL,
  type ChatChannel,
  type ChatNoticeKind,
} from "@/types/(my-space)/chat.interface";

/**
 * A channel's "Get messaged about": one switch per kind of notice. Off stops
 * that kind (as "No, stop sending" does); the whole channel's notifications
 * are its mute, above.
 */
export const ChannelNoticeSettings = ({ channel }: { channel: ChatChannel | undefined }) => {
  const { t } = useTranslation("chat");
  const settings = useGetChatSettingsQuery();
  const [setNotice] = useSetChannelNoticeMutation();
  const answers = settings.data?.channelNotices ?? {};

  const toggle = async (kind: ChatNoticeKind, keep: boolean) => {
    try {
      await setNotice({ kind, keep }).unwrap();
    } catch {
      toast.error(t("notice.answer_failed"));
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <div className="px-1">
        <h3 className="text-sm font-semibold text-foreground">{t("channel.settings.title")}</h3>
        <p className="text-xs text-muted-foreground">{t("channel.settings.subtitle")}</p>
      </div>
      <ul className="flex flex-col gap-1.5">
        {CHANNEL_NOTICE_KINDS[channel ?? DEFAULT_CHAT_CHANNEL].map((kind) => {
          const id = `channel-notice-${kind}`;
          return (
            <li key={kind} className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2.5">
              <label htmlFor={id} className="flex-1 text-sm text-foreground">
                {t(`notice.${kind}.setting`)}
              </label>
              <Switch
                id={id}
                // On unless stopped.
                checked={answers[kind] !== false}
                disabled={!settings.data}
                onCheckedChange={(on) => void toggle(kind, on)}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
};
